import { getSupabaseClient, isSupabaseConfigured } from '../lib/supabase';

export interface Service {
  id: string;
  name: string;
  description: string;
  longDescription: string;
  price: number;
  category: string;
  billingType: string;
  workDays?: number;
  features: string[];
  timeline: string;
  technicalDetails: {
    technologies: string[];
    [key: string]: unknown;
  };
  benefits: string[];
  popular: boolean;
  image?: string;
  pricingMode?: 'fixed' | 'starting-at';
  pricingNote?: string;
  optionSelectionMode?: 'multiple' | 'single';
  optionsTitle?: string;
  options?: ServiceOption[];
}

export interface ServiceOption {
  label: string;
  price: number;
  billingType?: string;
  description?: string;
  pricingMode?: 'additive' | 'replace-total';
}

export interface Category {
  id: string;
  name: string;
  slug: string;
  sortOrder: number;
}

interface CategoryRow {
  id: string;
  name: string;
  slug: string;
  sort_order: number | null;
}

interface ServiceRow {
  service_key: string;
  name: string;
  description: string | null;
  long_description: string | null;
  price: number | string | null;
  billing_type: string | null;
  work_days: number | null;
  features: unknown;
  timeline: string | null;
  technical_details: unknown;
  benefits: unknown;
  popular: boolean | null;
  image: string | null;
  category_name: string | null;
  csd_categories?: CategoryRow[] | null;
}

let servicesCache: Service[] | null = null;
let categoriesCache: Category[] | null = null;

const safeStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((entry): entry is string => typeof entry === 'string');
};

const safeServiceOptions = (value: unknown): ServiceOption[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
        return null;
      }

      const candidate = entry as Record<string, unknown>;
      const label = typeof candidate.label === 'string' ? candidate.label.trim() : '';
      const description =
        typeof candidate.description === 'string' ? candidate.description.trim() : '';
      const price = Number(candidate.price ?? 0);
      const billingType =
        typeof candidate.billingType === 'string' ? candidate.billingType.trim() : '';
      const pricingMode =
        candidate.pricingMode === 'replace-total' ? 'replace-total' : 'additive';

      if (!label) {
        return null;
      }

      return {
        label,
        price: Number.isFinite(price) ? price : 0,
        ...(description ? { description } : {}),
        ...(billingType ? { billingType } : {}),
        ...(pricingMode !== 'additive' ? { pricingMode } : {}),
      };
    })
    .filter((entry): entry is ServiceOption => Boolean(entry));
};

const normalizeTechnicalDetails = (value: unknown): Service['technicalDetails'] => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { technologies: [] };
  }

  const input = value as Record<string, unknown>;
  const technologies = safeStringArray(input.technologies);

  return {
    ...input,
    technologies,
  };
};

const slugify = (value: string): string => {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

const mapCategoryRow = (row: CategoryRow): Category => ({
  id: row.id,
  name: row.name,
  slug: row.slug,
  sortOrder: Number(row.sort_order ?? 0),
});

const mapServiceRow = (row: ServiceRow): Service => {
  const technicalDetails = normalizeTechnicalDetails(row.technical_details);
  const pricingMode =
    technicalDetails.pricingMode === 'starting-at' ? 'starting-at' : 'fixed';
  const pricingNote =
    typeof technicalDetails.pricingNote === 'string' ? technicalDetails.pricingNote : '';
  const optionSelectionMode =
    technicalDetails.optionSelectionMode === 'single' ? 'single' : 'multiple';
  const optionsTitle =
    typeof technicalDetails.optionsTitle === 'string' ? technicalDetails.optionsTitle : '';

  return {
    id: row.service_key,
    name: row.name,
    description: row.description ?? '',
    longDescription: row.long_description ?? '',
    price: Number(row.price ?? 0),
    category: row.csd_categories?.[0]?.name ?? row.category_name ?? 'Non classé',
    billingType: row.billing_type ?? 'one-time',
    workDays: row.work_days ?? undefined,
    features: safeStringArray(row.features),
    timeline: row.timeline ?? '',
    technicalDetails,
    benefits: safeStringArray(row.benefits),
    popular: Boolean(row.popular),
    image: row.image ?? undefined,
    pricingMode,
    ...(pricingNote ? { pricingNote } : {}),
    ...(optionSelectionMode === 'single' ? { optionSelectionMode } : {}),
    ...(optionsTitle ? { optionsTitle } : {}),
    ...(safeServiceOptions(technicalDetails.options).length
      ? { options: safeServiceOptions(technicalDetails.options) }
      : {}),
  };
};

const normalizeFallbackService = (service: Record<string, unknown>): Service => ({
  id: String(service.id ?? ''),
  name: String(service.name ?? ''),
  description: String(service.description ?? ''),
  longDescription: String(service.longDescription ?? ''),
  price: Number(service.price ?? 0),
  category: String(service.category ?? 'Non classé'),
  billingType: String(service.billingType ?? 'one-time'),
  ...(typeof service.workDays === 'number' ? { workDays: service.workDays } : {}),
  features: safeStringArray(service.features),
  timeline: String(service.timeline ?? ''),
  technicalDetails: normalizeTechnicalDetails(service.technicalDetails),
  benefits: safeStringArray(service.benefits),
  popular: Boolean(service.popular),
  ...(typeof service.image === 'string' && service.image ? { image: service.image } : {}),
  ...(service.pricingMode === 'starting-at' ? { pricingMode: 'starting-at' as const } : {}),
  ...(typeof service.pricingNote === 'string' && service.pricingNote
    ? { pricingNote: service.pricingNote }
    : {}),
  ...(service.optionSelectionMode === 'single'
    ? { optionSelectionMode: 'single' as const }
    : {}),
  ...(typeof service.optionsTitle === 'string' && service.optionsTitle
    ? { optionsTitle: service.optionsTitle }
    : {}),
  ...(safeServiceOptions(service.options).length
    ? { options: safeServiceOptions(service.options) }
    : {}),
});

const getFallbackData = async (): Promise<Service[]> => {
  const response = await fetch('/data/services.json');
  const data = await response.json();
  return Array.isArray(data?.services)
    ? data.services.map((service: Record<string, unknown>) => normalizeFallbackService(service))
    : [];
};

const buildFallbackCategories = (services: Service[]): Category[] => {
  return Array.from(
    new Map(
      services
        .map((service) => service.category.trim())
        .filter(Boolean)
        .sort((left, right) => left.localeCompare(right, 'fr'))
        .map((name, index) => [
          slugify(name),
          {
            id: slugify(name),
            name,
            slug: slugify(name),
            sortOrder: index,
          },
        ])
    ).values()
  );
};

export async function getAllCategories(forceRefresh = false): Promise<Category[]> {
  if (!forceRefresh && categoriesCache !== null) {
    return categoriesCache;
  }

  if (!isSupabaseConfigured()) {
    const fallbackServices = servicesCache ?? (await getFallbackData());
    servicesCache = fallbackServices;
    categoriesCache = buildFallbackCategories(fallbackServices);
    return categoriesCache;
  }

  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('csd_categories')
    .select('id,name,slug,sort_order')
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });

  if (error) {
    throw error;
  }

  const rows = (data ?? []) as CategoryRow[];
  categoriesCache = rows.map(mapCategoryRow);
  return categoriesCache;
}

export async function createCategory(name: string): Promise<Category> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase n\'est pas configuré.');
  }

  const trimmedName = name.trim();
  if (!trimmedName) {
    throw new Error('Le nom de catégorie est vide.');
  }

  const supabase = getSupabaseClient();
  const slug = slugify(trimmedName);

  const { data, error } = await supabase
    .from('csd_categories')
    .upsert(
      {
        name: trimmedName,
        slug,
      },
      { onConflict: 'slug' }
    )
    .select('id,name,slug,sort_order')
    .single();

  if (error) {
    throw error;
  }

  categoriesCache = null;
  return mapCategoryRow(data as CategoryRow);
}

const resolveCategoryId = async (categoryName: string): Promise<string | null> => {
  if (!categoryName.trim()) {
    return null;
  }

  const category = await createCategory(categoryName);
  return category.id;
};

export async function deleteCategory(categoryId: string): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase n\'est pas configuré.');
  }

  const supabase = getSupabaseClient();
  const { error } = await supabase.from('csd_categories').delete().eq('id', categoryId);

  if (error) {
    throw error;
  }

  categoriesCache = null;
}

export async function getAllServices(forceRefresh = false): Promise<Service[]> {
  if (!forceRefresh && servicesCache !== null) {
    return servicesCache;
  }

  if (!isSupabaseConfigured()) {
    servicesCache = await getFallbackData();
    return servicesCache;
  }

  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('csd_services')
    .select(`
      service_key,
      name,
      description,
      long_description,
      price,
      billing_type,
      work_days,
      features,
      timeline,
      technical_details,
      benefits,
      popular,
      image,
      category_name,
      csd_categories (
        id,
        name,
        slug,
        sort_order
      )
    `)
    .or('is_active.is.null,is_active.eq.true')
    .order('created_at', { ascending: true });

  if (error) {
    throw error;
  }

  const rows = (data ?? []) as ServiceRow[];
  servicesCache = rows.map(mapServiceRow);
  return servicesCache;
}

export async function getServicesByPage(
  page = 1,
  limit = 9
): Promise<{ total: number; services: Service[] }> {
  const allServices = await getAllServices();
  const start = (page - 1) * limit;
  const end = start + limit;

  return {
    total: allServices.length,
    services: allServices.slice(start, end),
  };
}

export function getServiceById(id: string): Service | null {
  if (!servicesCache) {
    return null;
  }

  return servicesCache.find((service) => service.id === id) ?? null;
}

export async function saveService(service: Service): Promise<void> {
  if (!isSupabaseConfigured()) {
    const currentServices = await getFallbackData();
    const nextServices = [
      ...currentServices.filter((entry) => entry.id !== service.id),
      normalizeFallbackService(service as unknown as Record<string, unknown>),
    ].sort((left, right) => {
      if (left.category === right.category) {
        return left.name.localeCompare(right.name, 'fr');
      }

      return left.category.localeCompare(right.category, 'fr');
    });

    const response = await fetch('/saveServices.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ services: nextServices }, null, 2),
    });

    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.status !== 'success') {
      throw new Error(payload?.message || 'Erreur de sauvegarde du catalogue');
    }

    servicesCache = null;
    categoriesCache = null;
    return;
  }

  const supabase = getSupabaseClient();
  const categoryId = await resolveCategoryId(service.category);

  const { error } = await supabase.from('csd_services').upsert(
    {
      service_key: service.id,
      name: service.name,
      description: service.description,
      long_description: service.longDescription,
      price: service.price,
      billing_type: service.billingType,
      work_days: service.workDays ?? null,
      features: service.features,
      timeline: service.timeline,
      technical_details: {
        ...service.technicalDetails,
        pricingMode: service.pricingMode ?? 'fixed',
        pricingNote: service.pricingNote ?? '',
        optionSelectionMode: service.optionSelectionMode ?? 'multiple',
        optionsTitle: service.optionsTitle ?? '',
        options: service.options ?? [],
      },
      benefits: service.benefits,
      popular: service.popular,
      image: service.image ?? null,
      category_id: categoryId,
      category_name: service.category,
      is_active: true,
    },
    { onConflict: 'service_key' }
  );

  if (error) {
    throw error;
  }

  servicesCache = null;
  categoriesCache = null;
}

export async function removeService(serviceKey: string): Promise<void> {
  if (!isSupabaseConfigured()) {
    const currentServices = await getFallbackData();
    const nextServices = currentServices.filter((service) => service.id !== serviceKey);

    const response = await fetch('/saveServices.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ services: nextServices }, null, 2),
    });

    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.status !== 'success') {
      throw new Error(payload?.message || 'Erreur de suppression du catalogue');
    }

    servicesCache = null;
    categoriesCache = null;
    return;
  }

  const supabase = getSupabaseClient();
  const { error } = await supabase
    .from('csd_services')
    .update({ is_active: false })
    .eq('service_key', serviceKey);

  if (error) {
    throw error;
  }

  servicesCache = null;
}

export function clearCatalogCache(): void {
  servicesCache = null;
  categoriesCache = null;
}
